import {
    useState
} from "react";
import {
    useWindowDimensions,
    StyleSheet,
    View
} from "react-native";
import type {
    LayoutChangeEvent
} from "react-native";
import {
    MenuTransition,
    SearchKitIcon,
    UnfoldKitIcon,
    FoldKitIcon,
    ThemeSwap,
    LogoImage
} from "./kitIcons";
import {
    NCoreUIKitTheme,
    NavigationMenu,
    SiteLogo,
    Button,
    Toggle,
    Badge,
    Tabs,
    Kbd
} from "ncore-ui-kit";
import {
    type DocsLanguage,
    useDocsI18n
} from "../i18n";
import {
    useDocsRouter
} from "../router";
import {
    type DocsThemePreference,
    useDocsLayout
} from "../theme";
import {
    isBlocksPath,
    BLOCKS_PATH
} from "../blocks";
import {
    CREATE_PATH
} from "../nav";

const THEME_ORDER: Array<DocsThemePreference> = [
    "light",
    "dark",
    "system"
];

const LANGUAGES: Array<DocsLanguage> = [
    "tr",
    "en"
];

const SEARCH_SHORTCUT = "mod+K";

const SEARCH_KEY_SHORTCUT = /Mac|iPhone|iPad/.test(navigator.platform) ? "Meta+K" : "Control+K";

type TopBarProps = {
    onThemePreferenceChange: (preference: DocsThemePreference) => void;
    themePreference: DocsThemePreference;
    onWidthToggle: () => void;
    onMenuToggle: () => void;
    onSearchOpen: () => void;
    isFullWidth: boolean;
    isMenuOpen: boolean;
    maxWidth?: number;
    version: string;
};

const stylesheet = StyleSheet.create({
    inner: {
        justifyContent: "flex-start",
        marginHorizontal: "auto",
        flexDirection: "row",
        alignItems: "center",
        width: "100%",
        flex: 1
    },
    header: {
        position: "sticky" as never,
        width: "100%",
        top: 0
    },
    searchShortcut: {
        transform: [
            {
                translateY: "-50%"
            }
        ],
        pointerEvents: "none",
        position: "absolute",
        top: "50%"
    },
    item: {
        justifyContent: "center",
        alignItems: "center"
    },
    searchWide: {
        justifyContent: "center"
    },
    spacer: {
        flex: 1
    }
});

const TopBar = ({
    onThemePreferenceChange,
    themePreference,
    onWidthToggle,
    onMenuToggle,
    onSearchOpen,
    isFullWidth,
    isMenuOpen,
    maxWidth,
    version
}: TopBarProps) => {
    const {
        setLanguage,
        language,
        t
    } = useDocsI18n();

    const {
        navigate,
        getHref,
        path
    } = useDocsRouter();

    const {
        controlHeights,
        shapeConfigs,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();

    const {
        headerHeight
    } = useDocsLayout();

    const isNarrowScreen = width < shapeConfigs.wideBreakpoint;

    const isCompactScreen = width < shapeConfigs.compactBreakpoint;

    const isBlocks = isBlocksPath(path);

    const isCreate = path === CREATE_PATH;

    const [
        shortcutSize,
        setShortcutSize
    ] = useState({
        height: 0,
        width: 0
    });

    const shortcutInset = shortcutSize.width ? (controlHeights.small - shortcutSize.height) / 2 : 0;

    const themeLabels: Record<DocsThemePreference, string> = {
        system: t("topbar.themeSystem"),
        light: t("topbar.themeLight"),
        dark: t("topbar.themeDark")
    };

    const themeLabel = t("topbar.themeLabel", [
        themeLabels[themePreference]
    ]);

    const handleThemePress = () => {
        const nextIndex = (THEME_ORDER.indexOf(themePreference) + 1) % THEME_ORDER.length;

        onThemePreferenceChange(THEME_ORDER[nextIndex] ?? "light");
    };

    const handleLanguageChange = (value: string) => {
        if(value === "tr" || value === "en") {
            setLanguage(value);
        }
    };

    const handleShortcutLayout = (event: LayoutChangeEvent) => {
        const {
            height,
            width
        } = event.nativeEvent.layout;

        setShortcutSize({
            height,
            width
        });
    };

    return <View
        style={[
            stylesheet.header,
            {
                backgroundColor: colors.content.container.default,
                borderBottomColor: colors.content.border.subtle,
                borderBottomWidth: borders.separator,
                zIndex: shapeConfigs.layers.sticky,
                height: headerHeight
            }
        ]}
        role="banner"
    >
        <View
            style={[
                stylesheet.inner,
                {
                    paddingHorizontal: isCompactScreen ? spaces.spacingSm : (isNarrowScreen ? spaces.spacingMd : spaces.spacingLg),
                    gap: isCompactScreen ? spaces.spacingXs : (isNarrowScreen ? spaces.spacingSm : spaces.spacingMd),
                    maxWidth: isFullWidth ? undefined : maxWidth
                }
            ]}
        >
            {
                isNarrowScreen && <MenuTransition.Provider
                    value={isMenuOpen}
                >
                    <View
                        style={stylesheet.item}
                    >
                        <Button
                            accessibilityLabel={isMenuOpen ? t("topbar.menuClose") : t("topbar.menuOpen")}
                            aria-controls="docs-sidebar"
                            aria-expanded={isMenuOpen}
                            icon={MenuTransition.Icon}
                            onPress={onMenuToggle}
                            variant="ghost"
                            type="neutral"
                            size="small"
                        />
                    </View>
                </MenuTransition.Provider>
            }
            <SiteLogo
                onPress={() => navigate("/")}
                subTitle={t("brand.sub")}
                isWorkWithAction={true}
                isWorkWithFlex1={false}
                imageSpace="spacingXs"
                image={LogoImage}
                title="NCore"
                size="small"
            />
            {
                !isNarrowScreen && <View
                    style={stylesheet.item}
                >
                    <Badge
                        variant="outline"
                        title={version}
                        type="neutral"
                        size="small"
                    />
                </View>
            }
            {
                !isCompactScreen && <NavigationMenu
                    title={t("topbar.navLabel")}
                    size="small"
                >
                    <NavigationMenu.Item
                        isActive={!isBlocks && !isCreate}
                        title={t("breadcrumb.docs")}
                        href={getHref("/")}
                        value="docs"
                    />
                    <NavigationMenu.Item
                        title={t("nav.sectionBlocks")}
                        href={getHref(BLOCKS_PATH)}
                        isActive={isBlocks}
                        value="blocks"
                    />
                    <NavigationMenu.Item
                        href={getHref(CREATE_PATH)}
                        title={t("create.nav")}
                        isActive={isCreate}
                        value="create"
                    />
                </NavigationMenu>
            }
            <View
                style={stylesheet.spacer}
            />
            {
                isNarrowScreen && <View
                    style={stylesheet.item}
                >
                    <Button
                        accessibilityLabel={t("topbar.search")}
                        aria-haspopup="dialog"
                        onPress={onSearchOpen}
                        icon={SearchKitIcon}
                        variant="ghost"
                        type="neutral"
                        size="small"
                    />
                </View>
            }
            {
                !isNarrowScreen && <View
                    style={stylesheet.searchWide}
                >
                    <Button
                        style={shortcutSize.width ? {
                            paddingEnd: shortcutInset + shortcutSize.width + spaces.spacingSm
                        } : undefined}
                        aria-keyshortcuts={SEARCH_KEY_SHORTCUT}
                        title={t("topbar.search")}
                        aria-haspopup="dialog"
                        onPress={onSearchOpen}
                        icon={SearchKitIcon}
                        variant="outline"
                        type="neutral"
                        size="small"
                    />
                    <View
                        style={[
                            stylesheet.searchShortcut,
                            {
                                end: shortcutInset
                            }
                        ]}
                        aria-hidden={true}
                    >
                        <Kbd
                            onLayout={handleShortcutLayout}
                            keys={SEARCH_SHORTCUT}
                            isInField={true}
                            size="small"
                        />
                    </View>
                </View>
            }
            <Tabs
                onValueChange={handleLanguageChange}
                listRole="radiogroup"
                variant="segment"
                value={language}
                size="small"
            >
                <Tabs.List
                    title={t("topbar.languageLabel")}
                >
                    {
                        LANGUAGES.map((item) => <Tabs.Trigger
                            title={item.toUpperCase()}
                            value={item}
                            key={item}
                        />)
                    }
                </Tabs.List>
            </Tabs>
            {
                !isNarrowScreen && <Toggle
                    accessibilityLabel={t("topbar.widthLabel")}
                    onPressedChange={onWidthToggle}
                    activeIcon={FoldKitIcon}
                    isPressed={isFullWidth}
                    icon={UnfoldKitIcon}
                    isIconOnly={true}
                    variant="ghost"
                    size="small"
                />
            }
            <View
                style={stylesheet.item}
            >
                <ThemeSwap.Provider
                    value={themePreference}
                >
                    <Button
                        accessibilityLabel={themeLabel}
                        onPress={handleThemePress}
                        icon={ThemeSwap.Icon}
                        variant="ghost"
                        type="neutral"
                        size="small"
                    />
                </ThemeSwap.Provider>
            </View>
        </View>
    </View>;
};
export default TopBar;
